// Saved views (design 02 3.6): a source (a tensor's name or a formula) that
// the engine calculates, shown as a table: each of its dimensions is a row
// (nested when more than one), a column, fixed to one item, or summed or
// averaged (합치기). No DOM here: the screen (ui/SavedViews.tsx), the CSV
// and the xlsx export (grid/viewsheets.ts) use these.
//
// Rules
//   - Summed: blanks are nothing (a cell with nothing at all is blank).
//     Averaged: the mean of the numbers there are, after summing. Text and
//     TRUE/FALSE are left out; an error in what a cell gathers is that
//     cell's value.
//   - A table of more than PIVOT_CELLS_MAX cells is not drawn: fix more.
//   - The chart: one dimension left (rows and columns) is a line or bars,
//     two are a heat map or grouped bars (lines over 월), a third is small
//     charts side by side, one each item, and the ones after it are fixed
//     to an item picked with a slider (chartData's `paged`).
import type { TensorSavedView, TensorViewConfig } from './api'
import type { CalcValue, EvalValue } from './calc/protocol'
import type { ChartData, Shape } from './chart'

export type AxisRole = 'row' | 'col' | 'fix' | 'sum' | 'mean'
export type ChartKind = NonNullable<TensorViewConfig['chart']>

export const ROLE_LABEL: Record<AxisRole, string> = { row: '행', col: '열', fix: '고정', sum: '합치기(합)', mean: '합치기(평균)' }
export const ROLES: AxisRole[] = ['row', 'col', 'fix', 'sum', 'mean']
export const PIVOT_CELLS_MAX = 20_000

/** What a view's source is, as named dimensions and row-major values. */
export interface Source {
  dims: { name: string; items: string[] }[]
  values: CalcValue[]
}

/** The engine's answer as a source: a grid array has the dimensions 행 and 열. */
export function sourceOf(v: EvalValue | undefined): Source | { error: string } {
  if (v === undefined) return { error: '#CALC!' }
  if (v !== null && typeof v === 'object') {
    if ('dims' in v) return { dims: v.dims.map((d) => ({ name: d.name, items: d.items })), values: v.values }
    if ('error' in v) return { error: v.error }
    if ('array' in v) {
      const rows = v.array.length
      const cols = v.array[0]?.length ?? 0
      const num = (n: number) => Array.from({ length: n }, (_, i) => String(i + 1))
      return { dims: [{ name: '행', items: num(rows) }, { name: '열', items: num(cols) }], values: v.array.flat() }
    }
  }
  return { dims: [], values: [v as CalcValue] }
}

export interface Axis {
  name: string
  role: AxisRole
  /** fix: the item */
  item?: string
}

/**
 * Each dimension's place: the saved one, else 월 (or the first) as rows,
 * the next as columns and the rest summed. A fixed one's item: the saved
 * one, else this month for 월, else the first. Order: config.order first.
 */
export function axesOf(src: Source, config: Pick<TensorViewConfig, 'axes' | 'order'>, thisMonth?: string): Axis[] {
  const names = src.dims.map((d) => d.name)
  const order = [...(config.order ?? []).filter((n) => names.includes(n)), ...names.filter((n) => !(config.order ?? []).includes(n))]
  const saved = config.axes ?? {}
  const unsaved = order.filter((n) => !saved[n])
  const firstRow = names.includes('월') && !saved['월'] ? '월' : unsaved[0]
  const firstCol = unsaved.find((n) => n !== firstRow)
  return order.map((name) => {
    const s = saved[name]
    const role: AxisRole = s ? s.role : name === firstRow ? 'row' : name === firstCol ? 'col' : 'sum'
    const axis: Axis = { name, role }
    if (role === 'fix') {
      const items = src.dims.find((d) => d.name === name)?.items ?? []
      axis.item =
        s?.item !== undefined && items.includes(s.item)
          ? s.item
          : name === '월' && thisMonth && items.includes(thisMonth)
            ? thisMonth
            : items[0]
    }
    return axis
  })
}

/** The settings saved for these places. */
export function configAxes(axes: Axis[]): Pick<TensorViewConfig, 'axes' | 'order'> {
  const out: NonNullable<TensorViewConfig['axes']> = {}
  for (const a of axes) out[a.name] = a.role === 'fix' && a.item !== undefined ? { role: a.role, item: a.item } : { role: a.role }
  return { axes: out, order: axes.map((a) => a.name) }
}

/**
 * A dimension moved to a place (dragged, or picked from its menu): before
 * `before` there, else last. A fixed one keeps its item; one newly fixed
 * gets the default (axesOf).
 */
export function moveAxis(axes: Axis[], name: string, role: AxisRole, before?: string): Axis[] {
  const a = axes.find((x) => x.name === name)
  if (!a || name === before) return axes
  const rest = axes.filter((x) => x.name !== name)
  const moved: Axis = role === 'fix' && a.role === 'fix' && a.item !== undefined ? { name, role, item: a.item } : { name, role }
  const at = before === undefined ? -1 : rest.findIndex((x) => x.name === before)
  return at < 0 ? [...rest, moved] : [...rest.slice(0, at), moved, ...rest.slice(at)]
}

/** The views not deleted, by name. */
export function savedViews(views: Record<number, TensorSavedView> | undefined): TensorSavedView[] {
  return Object.values(views ?? {})
    .filter((v) => !v.deleted)
    .sort((a, b) => a.name.localeCompare(b.name, 'ko') || a.id - b.id)
}

/** A view by its id (digits) or its name (any case of ASCII letters): /w/<id>/v/<view>. */
export function findView(views: Record<number, TensorSavedView> | undefined, key: string): TensorSavedView | undefined {
  if (/^\d+$/.test(key)) {
    const v = views?.[Number(key)]
    return v && !v.deleted ? v : undefined
  }
  const k = key.toLowerCase()
  return savedViews(views).find((v) => v.name.toLowerCase() === k)
}

export type PivotValue = number | string | null

export interface Pivot {
  rowDims: string[]
  colDims: string[]
  /** the items of each row (one per row dimension) and of each column */
  rows: string[][]
  cols: string[][]
  /** [row][column]: a number, an error's text, or blank */
  cells: PivotValue[][]
  /** each cell's item along every dimension of the source, when nothing is summed (editing) */
  places: Record<string, string>[][] | null
  /** more than PIVOT_CELLS_MAX cells: not drawn */
  tooBig: boolean
}

function product(lists: string[][]): string[][] {
  let out: string[][] = [[]]
  for (const l of lists) out = out.flatMap((p) => l.map((x) => [...p, x]))
  return out
}

/** The table of a source with its dimensions in these places. */
export function pivot(src: Source, axes: Axis[]): Pivot {
  const dimOf = new Map(src.dims.map((d, i) => [d.name, i]))
  const rowAxes = axes.filter((a) => a.role === 'row' && dimOf.has(a.name))
  const colAxes = axes.filter((a) => a.role === 'col' && dimOf.has(a.name))
  const items = (a: Axis) => src.dims[dimOf.get(a.name)!]!.items
  const rowLists = rowAxes.map(items)
  const colLists = colAxes.map(items)
  const nRows = rowLists.reduce((n, l) => n * l.length, 1)
  const nCols = colLists.reduce((n, l) => n * l.length, 1)
  const empty = { rowDims: rowAxes.map((a) => a.name), colDims: colAxes.map((a) => a.name) }
  if (nRows * nCols > PIVOT_CELLS_MAX) return { ...empty, rows: [], cols: [], cells: [], places: null, tooBig: true }
  const rows = product(rowLists)
  const cols = product(colLists)
  // for each source dimension: its place and the index along the output
  const role = src.dims.map((d) => axes.find((a) => a.name === d.name)?.role ?? 'sum')
  const fixAt = src.dims.map((d, i) => (role[i] === 'fix' ? d.items.indexOf(axes.find((a) => a.name === d.name)!.item ?? '') : -1))
  const rowAt = src.dims.map((d) => rowAxes.findIndex((a) => a.name === d.name))
  const colAt = src.dims.map((d) => colAxes.findIndex((a) => a.name === d.name))
  const meanDims = src.dims.map((_, i) => i).filter((i) => role[i] === 'mean')
  const nMean = meanDims.reduce((n, i) => n * src.dims[i]!.items.length, 1)
  const sums = new Map<number, { sum: number; any: boolean; error: string | null }>()
  const shape = src.dims.map((d) => d.items.length)
  const idx = new Array<number>(src.dims.length).fill(0)
  for (let flat = 0; flat < src.values.length; flat++) {
    if (flat > 0) {
      // the next index, last dimension fastest
      for (let k = shape.length - 1; k >= 0; k--) {
        idx[k]!++
        if (idx[k]! < shape[k]!) break
        idx[k] = 0
      }
    }
    let skip = false
    let r = 0
    let c = 0
    let m = 0
    for (let k = 0; k < shape.length && !skip; k++) {
      if (role[k] === 'fix' && idx[k] !== fixAt[k]) skip = true
      else if (rowAt[k]! >= 0) r = r * shape[k]! + idx[k]!
      else if (colAt[k]! >= 0) c = c * shape[k]! + idx[k]!
      else if (role[k] === 'mean') m = m * shape[k]! + idx[k]!
    }
    if (skip) continue
    // rows and columns nest in their axes' order, not the source's
    if (rowAxes.length > 1) r = nestedIndex(rowAxes, dimOf, shape, idx)
    if (colAxes.length > 1) c = nestedIndex(colAxes, dimOf, shape, idx)
    const key = (r * nCols + c) * nMean + m
    const v = src.values[flat]
    let acc = sums.get(key)
    if (!acc) sums.set(key, (acc = { sum: 0, any: false, error: null }))
    if (typeof v === 'number') {
      acc.sum += v
      acc.any = true
    } else if (v !== null && typeof v === 'object' && 'error' in v && acc.error === null) acc.error = v.error
  }
  const cells: PivotValue[][] = rows.map((_, r) =>
    cols.map((_, c) => {
      let total = 0
      let n = 0
      for (let m = 0; m < nMean; m++) {
        const acc = sums.get((r * nCols + c) * nMean + m)
        if (!acc) continue
        if (acc.error !== null) return acc.error
        if (acc.any) {
          total += acc.sum
          n++
        }
      }
      if (n === 0) return null
      return meanDims.length ? total / n : total
    }),
  )
  const summed = axes.some((a) => (a.role === 'sum' || a.role === 'mean') && dimOf.has(a.name))
  const places = summed
    ? null
    : rows.map((ri) =>
        cols.map((ci) => {
          const p: Record<string, string> = {}
          rowAxes.forEach((a, k) => (p[a.name] = ri[k]!))
          colAxes.forEach((a, k) => (p[a.name] = ci[k]!))
          for (const a of axes) if (a.role === 'fix' && a.item !== undefined) p[a.name] = a.item
          return p
        }),
      )
  return { ...empty, rows, cols, cells, places, tooBig: false }
}

function nestedIndex(axes: Axis[], dimOf: Map<string, number>, shape: number[], idx: number[]): number {
  let at = 0
  for (const a of axes) {
    const k = dimOf.get(a.name)!
    at = at * shape[k]! + idx[k]!
  }
  return at
}

/** The column headers of one level: an item over the columns it spans. */
export function headerSpans(cols: string[][], level: number): { at: number; n: number; label: string }[] {
  const out: { at: number; n: number; label: string }[] = []
  cols.forEach((c, j) => {
    const prev = out[out.length - 1]
    const first = prev ? cols[prev.at]! : null
    if (prev && first && c.slice(0, level + 1).every((x, k) => x === first[k])) prev.n++
    else out.push({ at: j, n: 1, label: c[level] ?? '' })
  })
  return out
}

/** The row headers as drawn: an outer item only where it changes. */
export function rowHeads(rows: string[][]): string[][] {
  return rows.map((r, i) =>
    r.map((x, k) => {
      if (k === r.length - 1 || i === 0) return x
      const prev = rows[i - 1]!
      return r.slice(0, k + 1).every((y, j) => y === prev[j]) ? '' : x
    }),
  )
}

// ---- charts ----

/** The dimensions a chart shows (rows, then columns). */
export function chartDims(axes: Axis[]): string[] {
  return [...axes.filter((a) => a.role === 'row'), ...axes.filter((a) => a.role === 'col')].map((a) => a.name)
}

/** The chart kind for "auto": time or few groups as lines and bars, many as a heat map. */
export function chartKind(kind: ChartKind | undefined, dims: string[], src: Source): Exclude<ChartKind, 'auto'> {
  const k = kind ?? 'auto'
  const n = dims.length
  if (n === 0) return 'none'
  if (k !== 'auto') {
    if (n === 1 && (k === 'heat' || k === 'groupbar')) return 'bar'
    if (n >= 2 && (k === 'line' || k === 'bar')) return k === 'line' ? 'line' : 'groupbar'
    return k
  }
  if (n === 1) return dims[0] === '월' ? 'line' : 'bar'
  const series = src.dims.find((d) => d.name === dims[1])?.items.length ?? 0
  return series <= 8 ? (dims[0] === '월' ? 'line' : 'groupbar') : 'heat'
}

/**
 * The chart's data: the first shown dimension along the bottom, the second
 * as series; the ones after it fixed to `paged` (an item each, default the
 * first: the small charts and the sliders). Values are the pivot's numbers
 * (errors and text left out).
 */
export function chartData(src: Source, axes: Axis[], paged: Record<string, string> = {}): ChartData {
  const dims = chartDims(axes)
  const a = dims[0]
  if (a === undefined) return { labels: [], series: [] }
  const b = dims[1]
  const placed: Axis[] = axes.map((x) => {
    if (x.name === a) return { name: x.name, role: 'row' }
    if (x.name === b) return { name: x.name, role: 'col' }
    if (dims.includes(x.name)) {
      const items = src.dims.find((d) => d.name === x.name)?.items ?? []
      const item = paged[x.name] !== undefined && items.includes(paged[x.name]!) ? paged[x.name] : items[0]
      return { name: x.name, role: 'fix', item }
    }
    return x
  })
  const p = pivot(src, placed)
  if (p.tooBig) return { labels: [], series: [] }
  const num = (v: PivotValue) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  return {
    labels: p.rows.map((r) => r[0] ?? ''),
    series: p.cols.map((c, j) => ({ name: c[0] ?? '값', values: p.cells.map((row) => num(row[j] ?? null)) })),
  }
}

/** A heat map: a box per (label, series), darker for more. */
export function heatShapes(data: ChartData, w: number, h: number): Shape[] {
  const out: Shape[] = []
  const left = 72
  const top = 8
  const bottom = 40
  const rows = data.series.length
  const cols = data.labels.length
  if (rows === 0 || cols === 0) {
    out.push({ kind: 'text', attrs: { x: w / 2, y: h / 2, 'text-anchor': 'middle', class: 'chart-empty' }, text: '숫자가 없습니다' })
    return out
  }
  const values = data.series.flatMap((s) => s.values.filter((v): v is number => v !== null))
  const lo = Math.min(0, ...values)
  const hi = Math.max(0, ...values)
  const cw = (w - left - 8) / cols
  const ch = (h - top - bottom) / rows
  data.series.forEach((s, i) => {
    out.push({
      kind: 'text',
      attrs: { x: left - 6, y: top + ch * (i + 0.5) + 4, 'text-anchor': 'end', class: 'chart-tick' },
      text: s.name.length > 8 ? s.name.slice(0, 7) + '…' : s.name,
    })
    s.values.forEach((v, j) => {
      const t = v === null || hi === lo ? 0 : (v - lo) / (hi - lo)
      out.push({
        kind: 'rect',
        attrs: {
          x: left + cw * j + 1,
          y: top + ch * i + 1,
          width: Math.max(1, cw - 2),
          height: Math.max(1, ch - 2),
          class: v === null ? 'heat-none' : 'heat-cell',
          'fill-opacity': v === null ? 1 : Number((0.08 + 0.92 * t).toFixed(3)),
        },
      })
    })
  })
  const every = Math.max(1, Math.ceil((cols * 40) / (w - left)))
  data.labels.forEach((l, j) => {
    if (j % every !== 0) return
    out.push({
      kind: 'text',
      attrs: { x: left + cw * (j + 0.5), y: h - bottom + 16, 'text-anchor': 'middle', class: 'chart-tick' },
      text: l.length > 8 ? l.slice(0, 7) + '…' : l,
    })
  })
  return out
}

// ---- CSV (design 02 8.3: a two-dimensional table) ----

function csvCell(v: PivotValue | undefined): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return String(v)
  // text that a spreadsheet would take for a formula gets a quote first
  const t = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v
  return /[",\n\r]/.test(t) || t !== v ? `"${t.replace(/"/g, '""')}"` : t
}

/**
 * The view's table as CSV, rows and columns as they are: a line per column
 * dimension (its name, then its items; the last one also names the row
 * dimensions, "월 \ 분류"), then a line per row (its items, then its
 * values). UTF-8 with a BOM, CRLF, as the 거래 CSV.
 */
export function pivotCsv(p: Pivot): string {
  const lines: PivotValue[][] = []
  const lead = Math.max(1, p.rowDims.length)
  const heads = p.rowDims.length ? [...p.rowDims] : ['']
  if (p.colDims.length === 0) lines.push([...heads, '값'])
  p.colDims.forEach((name, level) => {
    const head = new Array<string>(lead).fill('')
    if (level === p.colDims.length - 1 && p.rowDims.length) {
      heads.forEach((r, i) => (head[i] = r))
      head[lead - 1] = `${head[lead - 1]} \\ ${name}`
    } else head[lead - 1] = name
    lines.push([...head, ...p.cols.map((c) => c[level] ?? '')])
  })
  p.rows.forEach((r, i) => lines.push([...(p.rowDims.length ? r : ['']), ...p.cells[i]!]))
  return '﻿' + lines.map((l) => l.map(csvCell).join(',')).join('\r\n') + '\r\n'
}
